import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Share2, Check, Sun, Moon } from "lucide-react";
import { useI18n } from "../i18n";
import { LANGS } from "../langs";
import { shareSite } from "../share";
import LogoMark, { Wordmark } from "./LogoMark";

const scrollToCalc = () => {
    const el = document.getElementById("kalkulator");
    if (el) el.scrollIntoView({ behavior: "smooth" });
};

function LangSelect() {
    const { lang, setLang } = useI18n();
    const [open, setOpen] = useState(false);
    const cur = LANGS.find((l) => l.code === lang);
    return (
        <div className="relative">
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="lang-select"
                aria-label="Ganti bahasa"
                className="flex h-10 items-center gap-1.5 rounded-full border border-white/15 px-3 text-zinc-300 transition-colors duration-200 hover:border-white/40 hover:text-white"
            >
                <span className="text-base leading-none" aria-hidden="true">{cur.flag}</span>
                <span className="hidden text-xs font-bold uppercase sm:inline">{lang}</span>
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid="lang-backdrop" />
                    <div
                        className="absolute right-0 top-12 z-50 max-h-72 w-64 overflow-y-auto overscroll-contain rounded-2xl border border-white/10 bg-[#0d0a16]/95 p-2 shadow-glow backdrop-blur-xl"
                        data-testid="lang-menu"
                        data-lenis-prevent
                    >
                        {LANGS.map((l) => (
                            <button
                                key={l.code}
                                onClick={() => {
                                    setLang(l.code);
                                    setOpen(false);
                                }}
                                data-testid={`lang-item-${l.code}`}
                                className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-left text-sm transition-colors duration-150 ${
                                    l.code === lang ? "bg-white/10 font-bold text-white" : "text-zinc-300 hover:bg-white/5"
                                }`}
                            >
                                <span className="text-base leading-none" aria-hidden="true">{l.flag}</span>
                                {l.name}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function ThemeToggle() {
    const [theme, setTheme] = useState(() => localStorage.getItem("refundaja-theme") || "dark");
    useEffect(() => {
        document.documentElement.dataset.theme = theme;
        localStorage.setItem("refundaja-theme", theme);
    }, [theme]);
    return (
        <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle"
            aria-label="Ganti tema"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-zinc-300 transition-colors duration-200 hover:border-white/40 hover:text-white"
        >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
        </button>
    );
}

export default function Navbar() {
    const { t } = useI18n();
    const [scrolled, setScrolled] = useState(false);
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const share = async () => {
        const res = await shareSite(t.sub);
        if (res === "copied") {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <motion.header
            initial={{ y: -70, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
                scrolled ? "border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl" : "bg-transparent"
            }`}
            data-testid="navbar"
        >
            <nav className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
                <a href="#" className="flex items-center gap-2.5" data-testid="nav-logo">
                    <LogoMark size={30} glitch />
                    <Wordmark className="text-xl" />
                </a>
                <div className="hidden items-center gap-8 text-sm font-medium text-zinc-400 md:flex">
                    <a href="#kalkulator" className="transition-colors hover:text-white" data-testid="nav-link-kalkulator">
                        {t.n1}
                    </a>
                    <a href="#cara-kerja" className="transition-colors hover:text-white" data-testid="nav-link-cara-kerja">
                        {t.n2}
                    </a>
                    <a href="#faq" className="transition-colors hover:text-white" data-testid="nav-link-faq">
                        {t.n3}
                    </a>
                </div>
                <div className="flex items-center gap-2.5">
                    <button
                        onClick={share}
                        data-testid="nav-share"
                        aria-label={t.n5}
                        title={t.n5}
                        className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/15 text-zinc-300 transition-colors duration-200 hover:border-white/40 hover:text-white sm:flex"
                    >
                        {copied ? <Check size={16} className="text-emerald-400" /> : <Share2 size={16} />}
                    </button>
                    <ThemeToggle />
                    <LangSelect />
                    <button
                        onClick={scrollToCalc}
                        data-testid="nav-cta"
                        className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-coral via-brand-violet to-brand-blue px-4 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.04] active:scale-95 sm:px-5"
                    >
                        <span className="hidden sm:inline">{t.n4}</span>
                        <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                    </button>
                </div>
            </nav>
        </motion.header>
    );
}
